import React from "react";
import {
  Grid2,
  ContainerProps,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Card,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ClientWorkoutsChoose from "./ClientWorkoutsChoose";
import WorkoutCardBase from "./WorkoutCardBase";
import { parseWorkoutResultValueJsonToWorkoutSection } from "../workout/utils";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import WorkoutResultsService from "../../services/WorkoutResultsService";
import { CardPagination } from "../pagination/CardPagination";
import { WorkoutResultsSkeletonCard } from "../loading/WorkoutResultsSkeletonCard";
import { ReactComponent as WorkoutsIcon } from "../../icons/wokoutsIcon.svg";
import { ProfileIconBlock } from "../coach-clients/ProfileIconBlock";
import ClientDetailsCardHeader from "../coach-clients/ClientDetailsCardHeader";
import { JourneyAnchors } from "../../constants";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: "1 1 50%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
  },
  container: {
    minHeight: 100,
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1),
    },
  },
  hasMore: {
    margin: theme.spacing(0, 1),
  },
  noText: {
    fontSize: 16,
    color: theme.palette.text.secondary,
    width: "100%",
    textAlign: "center",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    marginTop: theme.spacing(1),
  },
}));

export interface ClientWorkoutsProps extends Omit<ContainerProps, "children"> {
  clientId: number;
}

const CLIENT_WORKOUTS_PAGE_SIZE_SM_UP = 4;
const CLIENT_WORKOUTS_PAGE_SIZE_SM = 3;
export const CLIENT_WORKOUTS_LIST_QUERY_KEY = "client-workouts-list";

function ClientWorkouts(props: ClientWorkoutsProps) {
  const { clientId } = props;

  const [selectWorkout, setSelectWorkout] = React.useState(null);
  const [isOpenContent, setIsOpenContent] = React.useState(true);

  const chooseWorkoutItem = React.useCallback(
    (data) => setSelectWorkout(data),
    [],
  );

  const handleCloseWorkout = React.useCallback(
    () => setSelectWorkout(null),
    [],
  );

  const sumSessions = React.useCallback((value) => {
    let sum = 0;
    value.exercises.forEach(({ result }) => {
      sum += result.length;
    });
    return sum;
  }, []);

  const sumLbs = React.useCallback((value) => {
    let lbs = 0;
    value.exercises.forEach(({ result }) => {
      const summ = Array.isArray(result)
        ? result
            .map((i) => (i.weight === "-" || !i.weight ? 0 : i.weight))
            .reduce((prev, curr) => +prev + +curr, 0)
        : result;
      lbs += summ;
    });
    return lbs;
  }, []);

  const timeWorkout = React.useCallback((value) => {
    const timeWorkout = value ? JSON.parse(value) : { time: 0 };
    return timeWorkout.time;
  }, []);

  const { breakpoints } = useTheme();
  const smUp = useMediaQuery(breakpoints.up("sm"));
  const pageSize = smUp
    ? CLIENT_WORKOUTS_PAGE_SIZE_SM_UP
    : CLIENT_WORKOUTS_PAGE_SIZE_SM;
  const [currentPage, setCurrentPage] = React.useState(1);

  const {
    data,
    isFetching,
    isPlaceholderData: isPrevPageData,
    isLoading: isFirstPageFirstFetch,
  } = useQuery({
    // keep old data to display pagination
    placeholderData: keepPreviousData,
    queryKey: [CLIENT_WORKOUTS_LIST_QUERY_KEY, { clientId, currentPage }],
    queryFn: () =>
      WorkoutResultsService.getUserExerciseResults(
        currentPage,
        pageSize,
        clientId,
      ),
  });

  const s = useStyles();

  const workoutExerciseResults = React.useMemo(
    () =>
      data?.items?.map(({ value, activity, ...dto }) => ({
        ...dto,
        activity: {
          ...activity,
          completedAt: activity.completedAt.format("DD-MM-YYYY"),
        },
        section: parseWorkoutResultValueJsonToWorkoutSection(value),
      })),
    [data],
  );

  const showCards = workoutExerciseResults?.length > 0 && !isPrevPageData;
  const showSkeleton = isFirstPageFirstFetch || (isFetching && isPrevPageData);
  const showEmpty = !showCards && !showSkeleton;

  return (
    <Card
      className={clsx(s.root, JourneyAnchors.CLIENT_WORKOUTS)}
      elevation={0}
      variant="outlined"
    >
      <ClientDetailsCardHeader
        title="Workouts"
        icon={<ProfileIconBlock icon={<WorkoutsIcon />} bgVariant="accent" />}
        isOpenContent={isOpenContent}
        setIsOpenContent={setIsOpenContent}
      />
      {isOpenContent && (
        <Grid2 container spacing={2} className={s.container}>
          {selectWorkout ? (
            <ClientWorkoutsChoose
              onClose={handleCloseWorkout}
              selectWorkout={selectWorkout}
            />
          ) : (
            <>
              {showCards &&
                workoutExerciseResults.map(({ activity, section }, index) => (
                  <Box key={index} sx={{ width: "100%" }}>
                    <WorkoutCardBase
                      title={activity.component?.title}
                      subtitle={`Recorded ${activity.completedAt}`}
                      onClick={() =>
                        chooseWorkoutItem({
                          value: section.exercises,
                          session: sumSessions(section),
                          min: timeWorkout(activity.endWorkout),
                          lbs: sumLbs(section),
                          title: activity.component?.title,
                          date: activity.completedAt,
                        })
                      }
                      footerItem={{
                        session: sumSessions(section),
                        min: timeWorkout(activity.endWorkout),
                      }}
                    />
                  </Box>
                ))}

              {showSkeleton &&
                Array.from({ length: pageSize }).map((_, i) => (
                  <WorkoutResultsSkeletonCard key={i} />
                ))}

              {showEmpty && (
                <Typography className={s.noText}>
                  No workouts to show.
                </Typography>
              )}

              {data?.totalPages > 1 && (
                <CardPagination
                  page={currentPage}
                  count={data?.totalPages}
                  siblingCount={1}
                  boundaryCount={1}
                  className={s.pagination}
                  onChange={(_, page) => setCurrentPage(page)}
                />
              )}
            </>
          )}
        </Grid2>
      )}
    </Card>
  );
}

export default React.memo(ClientWorkouts);
